import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Checkbox, message, Typography } from "antd";
import { loginUser } from "../../services/auth.service";
import { setAuthData, clearAuthData } from "../../utils/storage";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import logo from "../../assets/ezygo-logo.png";
import "./login.css";

const { Title } = Typography;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [form] = Form.useForm();

  const redirectPath = location.state?.from || "/dashboard";

  const handleLogin = async (values) => {
    if (loading) return;

    setLoading(true);
    try {
      clearAuthData();

      const response = await loginUser({
        email: values.email.trim().toLowerCase(),
        password: values.password.trim(),
      });

      // Check if user needs to change password
      if (response.forceReset) {
        setAuthData({
          token: response.token,
          user: response.user,
          remember: values.remember,
        });
        setLoginData({ remember: values.remember });
        setShowChangePassword(true);
      } else {
        setAuthData({
          token: response.token,
          user: response.user,
          remember: values.remember,
        });
        navigate(redirectPath, { replace: true });
      }
    } catch (err) {
      const errorMsg =
        err?.errors?.[0]?.msg || err?.message || "Invalid email or password";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowChangePassword(false);
    navigate(redirectPath, { replace: true });
  };

  return (
    <>
      <div style={{ width: 360 }}>
        <img src={logo} alt="EZYGO" className="login-logo" />
        <Title level={2} style={{ marginBottom: 24 }}>
          Welcome back
        </Title>

        <Form
        form={form}
        layout="vertical"
        className="login-form"
        onFinish={handleLogin}
        requiredMark={false}
        onFieldsChange={() => {
          const hasErrors = form
            .getFieldsError()
            .some(({ errors }) => errors.length);

          setIsFormValid(!hasErrors);
        }}
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input
            placeholder="Enter email"
          />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: true, message: "Password is required" }]}
        >
          <Input.Password
            placeholder="Enter password"
          />
        </Form.Item>

        <Form.Item name="remember" valuePropName="checked">
          <Checkbox>Remember me</Checkbox>
        </Form.Item>

        <Button
          type={!isFormValid || loading ? "dashed" : "primary"}
          htmlType="submit"
          block
          loading={loading}
          disabled={!isFormValid || loading}
        >
          Login
        </Button>

        <Button
          type="link"
          onClick={() => navigate("/forgot-password")}
          style={{ textAlign: "left", paddingLeft: 0 }}
        >
          Forgot password?
        </Button>
      </Form>
    </div>

    <ChangePasswordModal
      open={showChangePassword}
      onSuccess={handlePasswordChangeSuccess}
    />
  </>
  );
}